import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AdminTables from "@/pages/admin/AdminTables";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminUpi from "@/pages/admin/AdminUpi";

const DEFAULTS = {
  tax_mode: "exclusive",
  tax_percent: 5,
  points_per_rupee: 0.1,
  redeem_rate: 0.1,
  min_redeem: 50,
  max_redeem_pct: 50,
  currency: "INR",
};

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setS({ ...DEFAULTS, ...r.data }))
      .catch(() => setS({ ...DEFAULTS }));
  }, []);

  if (!s) return <div className="text-sm text-gray-400">Loading settings…</div>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const num = (k, e) => set(k, e.target.value === "" ? "" : Number(e.target.value));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        tax_mode: s.tax_mode,
        tax_percent: Number(s.tax_percent) || 0,
        points_per_rupee: Number(s.points_per_rupee) || 0,
        redeem_rate: Number(s.redeem_rate) || 0,
        min_redeem: parseInt(s.min_redeem, 10) || 0,
        max_redeem_pct: parseInt(s.max_redeem_pct, 10) || 0,
        currency: s.currency || "INR",
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="max-w-2xl">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-400">Configuration</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Settings</h1>
      </header>

      {/* Tax / GST */}
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="font-semibold">Tax / GST</h2>
        <p className="text-sm text-gray-500 mb-4">How GST applies to your menu prices.</p>
        <div className="space-y-3">
          <ModeOption
            active={s.tax_mode === "exclusive"} onClick={() => set("tax_mode", "exclusive")}
            title="Add GST on top" desc="Menu prices are pre-tax. GST is added at checkout — the customer pays price + GST." />
          <ModeOption
            active={s.tax_mode === "inclusive"} onClick={() => set("tax_mode", "inclusive")}
            title="GST included in price" desc="Menu prices already include GST. The bill shows the embedded GST for your records." />
          <div>
            <label className="text-sm font-medium">GST rate (%)</label>
            <Input data-testid="settings-tax-percent" type="number" min="0" max="100" step="0.01"
              value={s.tax_percent} onChange={(e) => num("tax_percent", e)} className="mt-1 max-w-[140px] rounded-lg" />
          </div>
        </div>
      </section>

      {/* Loyalty */}
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="font-semibold">Loyalty</h2>
        <p className="text-sm text-gray-500 mb-4">Points earned per rupee, and redemption limits.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Points per ₹1 spent" hint="0.1 = 10 pts per ₹100">
            <Input type="number" step="0.01" min="0" value={s.points_per_rupee} onChange={(e) => num("points_per_rupee", e)} className="mt-1 rounded-lg" />
          </Field>
          <Field label="₹ value per point" hint="0.1 = ₹10 per 100 pts">
            <Input type="number" step="0.01" min="0" value={s.redeem_rate} onChange={(e) => num("redeem_rate", e)} className="mt-1 rounded-lg" />
          </Field>
          <Field label="Min points to redeem">
            <Input type="number" min="0" value={s.min_redeem} onChange={(e) => num("min_redeem", e)} className="mt-1 rounded-lg" />
          </Field>
          <Field label="Max redemption (% of bill)">
            <Input type="number" min="0" max="100" value={s.max_redeem_pct} onChange={(e) => num("max_redeem_pct", e)} className="mt-1 rounded-lg" />
          </Field>
        </div>
      </section>

      <Button data-testid="settings-save" onClick={save} disabled={saving}
        className="rounded-lg bg-[var(--qd-charcoal)] hover:bg-black text-white">
        {saving ? "Saving…" : "Save settings"}
      </Button>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <AdminTables />
      </div>
      <AdminUpi />
      <AdminOffers />
    </div>
  );
}

function ModeOption({ active, onClick, title, desc }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition-colors ${active ? "border-[var(--qd-primary)] bg-orange-50" : "border-gray-200 hover:bg-gray-50"}`}>
      <span className={`mt-0.5 w-4 h-4 rounded-full border-2 grid place-items-center ${active ? "border-[var(--qd-primary)]" : "border-gray-300"}`}>
        {active && <span className="w-2 h-2 rounded-full bg-[var(--qd-primary)]" />}
      </span>
      <span>
        <span className="block font-medium text-sm">{title}</span>
        <span className="block text-xs text-gray-500">{desc}</span>
      </span>
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
    </div>
  );
}
